frontApp.utilities = {
  /**
   * Get a value of offset top coordinate
   * @param  {Object} element DOM element
   * @return {Number}         Coordinate offset top
   */
  getOffsetTop(element) {
    if (element === null) return 0;
    let el = element;
    let y = 0;
    while (el && !Number.isNaN(el.offsetTop)) {
      y += el.offsetTop - el.scrollTop;
      el = el.offsetParent;
    }
    return y;
  },

  /**
   * Get a children nodes by selector
   * @param  {Object} element  DOM element
   * @param  {String} selector CSS selector
   * @return {Array}           Array of node elements
   */
  children(element, selector) {
    return [].concat(...element.children)
      .filter((child) => child.matches(selector));
  },

  /**
   * Get a siblings nodes
   * @param  {Object} element  DOM element
   * @return {Array}           Array of node elements
   */
  siblings(element) {
    const siblings = [];
    if (!element.parentNode) {
      return siblings;
    }
    let sibling = element.parentNode.firstChild;
    while (sibling) {
      if (sibling.nodeType === 1 && sibling !== element) {
        siblings.push(sibling);
      }
      sibling = sibling.nextSibling;
    }
    return siblings;
  },

  /**
   * Trick to restart an element's animation
   *
   * @param {HTMLElement} element
   * @return void
   *
   * @see https://www.charistheo.io/blog/2021/02/restart-a-css-animation-with-javascript/#restarting-a-css-animation
   */
  reflow(element) {
    // eslint-disable-next-line no-unused-expressions
    element.offsetHeight;
  },

  /**
   * callAfterTransition
   */
  callAfterTransition(callback, transitionElement) {
    const timeout = 5;
    const emulatedDuration = this.getTransitionDuration(transitionElement) + timeout;

    let called = false;

    const handler = ({ target }) => {
      if (target !== transitionElement) {
        return;
      }

      called = true;
      transitionElement.removeEventListener('transitionend', handler);
      this.callImmediately(callback);
    };

    transitionElement.addEventListener('transitionend', handler);
    setTimeout(() => {
      if (!called) {
        this.triggerTransitionEnd(transitionElement);
      }
    }, emulatedDuration);
  },

  /**
   * callImmediately
   */
  callImmediately(callback) {
    if (typeof callback === 'function') {
      callback();
    }
  },

  /**
   * getTransitionDuration
   */
  getTransitionDuration(element) {
    if (!element) {
      return 0;
    }

    // Get transition-duration of the element
    let { transitionDuration, transitionDelay } = window.getComputedStyle(element);

    const floatTransitionDuration = Number.parseFloat(transitionDuration);
    const floatTransitionDelay = Number.parseFloat(transitionDelay);

    // Return 0 if element or transition duration is not found
    if (!floatTransitionDuration && !floatTransitionDelay) {
      return 0;
    }

    // If multiple durations are defined, take the first
    [transitionDuration] = transitionDuration.split(',');
    [transitionDelay] = transitionDelay.split(',');

    return (Number.parseFloat(transitionDuration)
        + Number.parseFloat(transitionDelay)) * 1000;
  },

  /**
   * triggerTransitionEnd
   */
  triggerTransitionEnd(element) {
    element.dispatchEvent(new Event('transitionend'));
  },

  /**
   * Throttle function
   * @param {Function} func Function
   * @param {Number} ms     Timeout in milliseconds
   * @return {Function}     Function
   */
  throttle(func, ms) {
    let lastCall = 0;
    return function (args) {
      const previousCall = lastCall;
      lastCall = Date.now();
      if (previousCall === undefined || (lastCall - previousCall) > ms) {
        func(args);
      }
    };
  },

  /**
   * Debounce function
   * @param {Function} func Function
   * @param {Number} ms     Timeout in milliseconds
   * @return {Function}     Function
   */
  debounce(func, ms) {
    let lastCall = 0;
    let lastCallTimer = null;
    return function (args) {
      const previousCall = lastCall;
      lastCall = Date.now();
      if (previousCall && ((lastCall - previousCall) <= ms)) {
        clearTimeout(lastCallTimer);
      }
      lastCallTimer = setTimeout(() => func(args), ms);
    };
  },

  /**
   * https://learn.javascript.ru/cookie#getcookie-name
   * @param name
   * @returns {string|undefined}
   */
  getCookie(name) {
    // eslint-disable-next-line
    const matches = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1')}=([^;]*)`));
    return matches ? decodeURIComponent(matches[1]) : undefined;
  },

  /**
   * https://learn.javascript.ru/cookie#getcookie-name
   * @param name
   * @param value
   * @param options
   */
  // eslint-disable-next-line
  setCookie(name, value, options = {}) {
    const opts = {
      path: '/',
      ...options,
    };

    if (opts.expires instanceof Date) {
      opts.expires = opts.expires.toUTCString();
    }

    let updatedCookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    // eslint-disable-next-line
    for (const optionKey in opts) {
      updatedCookie += `; ${optionKey}`;
      const optionValue = opts[optionKey];
      if (optionValue !== true) {
        updatedCookie += `=${optionValue}`;
      }
    }

    document.cookie = updatedCookie;
  },
};
